import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Box, Chip, IconButton, Stack, Switch, Typography } from '@mui/material';
import React from 'react';
import { IVerbaScholaLesson, IVerbaScholaLessonGroup, IVerbaScholaSummary } from '../api/VerbaScholaApi';
import { VerbaScholaEmptyState } from '../component/VerbaScholaTeacherShell';
import { BLUE, BLUE_TEXT, BORDER, GREEN, INTER, MUTED, NAVY, POPPINS, SLATE, SOFT_BLUE, SUCCESS_TEXT, SURFACE_TINT, WARNING, WARNING_TEXT } from '../style/verbaScholaStyles';
import { lessonSummaryLine } from '../util/lessonSummary';

function completedStudentCount(students: IVerbaScholaLessonGroup['students']): number {
    return students.filter(student => Number.isFinite(student.completion_percent) && student.completion_percent >= 100).length;
}

function lessonStatusColor(status: string): { color: string; textColor: string } {
    if (status === 'published' || status === 'current') return { color: BLUE, textColor: BLUE_TEXT };
    if (status === 'complete') return { color: GREEN, textColor: SUCCESS_TEXT };
    return { color: WARNING, textColor: WARNING_TEXT };
}

function lessonStatusLabel(status: string): string {
    if (status === 'published') return 'Published';
    if (status === 'current') return 'Current';
    if (status === 'draft') return 'Draft';
    return status.charAt(0).toUpperCase() + status.slice(1);
}

function lessonAvailable(lesson: IVerbaScholaLesson): boolean {
    return !lesson.is_hidden && !lesson.is_archived && ['published', 'current', 'complete'].includes(lesson.status);
}

function lessonAvailabilityLabel(lesson: IVerbaScholaLesson): string {
    if (lesson.is_archived) return 'Archived lesson';
    if (lesson.status === 'draft') return 'Unpublished draft';
    return lesson.is_hidden || lesson.is_archived ? 'Hidden from students' : 'Available to students';
}

function lessonCompletionNote(group: IVerbaScholaLessonGroup | undefined, studentCount: number): string {
    if (!group) return studentCount === 0 ? 'No students enrolled' : 'No progress yet';
    return `${completedStudentCount(group.students)}/${studentCount} students completed`;
}

function sortedLessons(lessons: IVerbaScholaLesson[]): IVerbaScholaLesson[] {
    return [...lessons].sort((a, b) => {
        const left = a.order_index ?? Number.MIN_SAFE_INTEGER;
        const right = b.order_index ?? Number.MIN_SAFE_INTEGER;
        return left === right ? a.id - b.id : left - right;
    });
}

interface Props {
    lessons: IVerbaScholaLesson[];
    lessonGroupsById: Map<number, IVerbaScholaLessonGroup>;
    summary: IVerbaScholaSummary | undefined;
    saving: boolean;
    onReorder: (lessons: IVerbaScholaLesson[]) => void;
    onSetLessonAvailable: (lesson: IVerbaScholaLesson, available: boolean) => void;
    onOpenLesson: (lessonId: number) => void;
}

export default function VerbaScholaTeacherCourseLessonsPage({ lessons, lessonGroupsById, summary, saving, onReorder, onSetLessonAvailable: handleSetLessonAvailable, onOpenLesson }: Props) {
    const orderedLessons = sortedLessons(lessons);
    const [draggedLessonId, setDraggedLessonId] = React.useState<number | null>(null);
    const [dragOverLessonId, setDragOverLessonId] = React.useState<number | null>(null);

    function moveLesson(lessonId: number, direction: -1 | 1) {
        if (saving) return;
        const index = orderedLessons.findIndex(lesson => lesson.id === lessonId);
        const target = index + direction;
        if (index < 0 || target < 0 || target >= orderedLessons.length) return;
        const reordered = [...orderedLessons];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        onReorder(reordered);
    }

    function handleLessonDrop(targetLessonId: number) {
        if (draggedLessonId === null || draggedLessonId === targetLessonId) {
            setDraggedLessonId(null); setDragOverLessonId(null); return;
        }
        const fromIndex = orderedLessons.findIndex(lesson => lesson.id === draggedLessonId);
        const toIndex = orderedLessons.findIndex(lesson => lesson.id === targetLessonId);
        if (fromIndex < 0 || toIndex < 0) {
            setDraggedLessonId(null); setDragOverLessonId(null); return;
        }
        const reordered = [...orderedLessons];
        const [movedLesson] = reordered.splice(fromIndex, 1);
        reordered.splice(toIndex, 0, movedLesson);
        setDraggedLessonId(null); setDragOverLessonId(null);
        onReorder(reordered);
    }

    return (
        <Box sx={{ backgroundColor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '20px', overflow: 'hidden' }}>
                            <Box sx={{ p: 2.5, borderBottom: `1px solid ${BORDER}` }}>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>Class lessons</Typography>
                            </Box>
                            {orderedLessons.length === 0 ? (
                                <VerbaScholaEmptyState message="This class has no lessons yet." />
                            ) : (
                                <Stack spacing={1.5} sx={{ p: 2.5 }}>
                                    {orderedLessons.map((lesson, index) => {
                                        const statusColor = lessonStatusColor(lesson.status);
                                        const available = lessonAvailable(lesson);
                                        const group = lessonGroupsById.get(lesson.id);
        
                                        return (
                                        <Box
                                            key={lesson.id}
                                            onDragOver={event => {
                                                event.preventDefault();
                                                event.dataTransfer.dropEffect = 'move';
                                                setDragOverLessonId(lesson.id);
                                            }}
                                            onDrop={event => {
                                                event.preventDefault();
                                                handleLessonDrop(lesson.id);
                                            }}
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '26px 44px minmax(0, 1fr) 38px', md: '26px 48px minmax(0, 1fr) 105px 185px 38px' },
                                                gap: 1.25,
                                                alignItems: 'center',
                                                p: 1.75,
                                                border: `1px solid ${dragOverLessonId === lesson.id && draggedLessonId !== lesson.id ? BLUE : BORDER}`,
                                                borderRadius: '15px',
                                                backgroundColor: available ? '#fff' : SURFACE_TINT,
                                                opacity: draggedLessonId === lesson.id ? 0.55 : 1,
                                                transition: 'border-color 120ms ease, opacity 120ms ease',
                                            }}
                                        >
                                            <Box
                                                draggable
                                                onDragStart={event => {
                                                    event.dataTransfer.effectAllowed = 'move';
                                                    event.dataTransfer.setData('text/plain', String(lesson.id));
                                                    setDraggedLessonId(lesson.id);
                                                }}
                                                onDragEnd={() => {
                                                    setDraggedLessonId(null);
                                                    setDragOverLessonId(null);
                                                }}
                                                role="button"
                                                tabIndex={0}
                                                aria-label={`Reorder ${lesson.title}. Press up or down arrow to move it.`}
                                                onKeyDown={event => {
                                                    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                                                        event.preventDefault();
                                                        moveLesson(lesson.id, event.key === 'ArrowUp' ? -1 : 1);
                                                    }
                                                }}
                                                sx={{ display: 'grid', placeItems: 'center', alignSelf: 'stretch', borderRadius: '9px', color: MUTED, cursor: 'grab', '&:hover': { color: BLUE, backgroundColor: SOFT_BLUE }, '&:active': { cursor: 'grabbing' } }}
                                            >
                                                <DragIndicatorRoundedIcon sx={{ fontSize: 21 }} />
                                            </Box>
                                            <Box sx={{ width: 42, height: 42, borderRadius: '13px', display: 'grid', placeItems: 'center', backgroundColor: `${statusColor.color}12`, color: statusColor.textColor, fontFamily: POPPINS, fontWeight: 700 }}>
                                                {index + 1}
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 13.5, color: NAVY }}>{lesson.title}</Typography>
                                                {lessonSummaryLine(lesson) && (
                                                    <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE, mt: 0.25 }}>{lessonSummaryLine(lesson)}</Typography>
                                                )}
                                            </Box>
                                            <Chip label={lesson.is_archived ? 'Archived' : lessonStatusLabel(lesson.status)} size="small" sx={{ display: { xs: 'none', md: 'flex' }, justifySelf: 'start', fontFamily: INTER, fontWeight: 700, fontSize: 11, color: statusColor.textColor, backgroundColor: `${statusColor.color}12` }} />
                                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
                                                <Switch size="small" checked={available} disabled={saving || lesson.is_archived} onChange={event => handleSetLessonAvailable(lesson, event.target.checked)} inputProps={{ 'aria-label': `${lesson.title} available to students` }} />
                                                <Box>
                                                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: available ? NAVY : SLATE }}>Available to students</Typography>
                                                    <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE, mt: 0.1 }}>{available ? lessonCompletionNote(group, summary?.student_count ?? 0) : lessonAvailabilityLabel(lesson)}</Typography>
                                                </Box>
                                            </Stack>
                                            <IconButton size="small" aria-label={`Open ${lesson.title}`} onClick={() => onOpenLesson(lesson.id)} sx={{ color: BLUE }}>
                                                <ChevronRightRoundedIcon />
                                            </IconButton>
                                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ display: { xs: 'flex', md: 'none' }, gridColumn: '3 / -1', mt: 0.25 }}>
                                                <Switch size="small" checked={available} disabled={saving || lesson.is_archived} onChange={event => handleSetLessonAvailable(lesson, event.target.checked)} inputProps={{ 'aria-label': `${lesson.title} available to students on mobile` }} />
                                                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: available ? NAVY : SLATE }}>{lessonAvailabilityLabel(lesson)}</Typography>
                                            </Stack>
                                        </Box>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Box>
    );
}
